import { ClipLoader } from 'react-spinners';
import '../../styles/DiagnosisCodesPanel.css';
import ReviewStamp from './ReviewStamp';

// Review panel for AI- and user-selected ICD-10-CM diagnosis codes.
// Presentational: the parent owns persistence of status
// ('selected'|'rejected'|pending), primary_index, and user-added
// candidates, mirroring how RedFlagsAlert defers to EditCase.
export default function DiagnosisCodesPanel({
    diagnosisCodes,
    onCodeReview,
    onDesignatePrimary,
    onCopyCodes,
    copySuccess,
    onCopyQuery,
    copiedQueryIndex,
    onLookupCode,
    onRefine,
    onRetryEvidence,
}) {
    if (!diagnosisCodes || !Array.isArray(diagnosisCodes.candidates)) return null;

    const { candidates, table_fy: tableFy, primary_index: primaryIndex, verified } = diagnosisCodes;
    const codeCandidates = candidates.filter(c => c.code);
    const gaps = candidates.filter(c => !c.code && c.missing_fact);
    const selectedCount = codeCandidates.filter(c => c.status === 'selected').length;
    const comorbidityCount = candidates.filter(c => c.validation?.comorbidity_subgroup).length;
    const primary = typeof primaryIndex === 'number' ? candidates[primaryIndex] : null;
    const primaryUnacceptable = primary && !(primary.validation?.acceptable_primary);

    if (candidates.length === 0) {
        return (
            <div className="dx-codes-empty">
                No diagnosis codes suggested from the uploaded documents.
                {onLookupCode && (
                    <button className="dx-codes-copy dx-codes-add" onClick={onLookupCode}>
                        Look up code
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="dx-codes-panel">
            <div className="dx-codes-header">
                <span className="dx-codes-title">Suggested ICD-10-CM Diagnosis Codes</span>
                <span className="dx-codes-meta">
                    FY{tableFy} CMS tables &middot; {selectedCount} of {codeCandidates.length} selected
                    {comorbidityCount > 0 && (
                        <> &middot; {comorbidityCount} with comorbidity subgroups</>
                    )}
                </span>
                {onLookupCode && (
                    <button className="dx-codes-copy dx-codes-add" onClick={onLookupCode}>
                        Look up code
                    </button>
                )}
                {onCopyCodes && (
                    <button className="dx-codes-copy" onClick={onCopyCodes}>
                        {copySuccess ? 'Copied!' : 'Copy code list'}
                    </button>
                )}
            </div>

            {verified === false && (
                <div className="dx-codes-warning dx-codes-warning--unverified">
                    &#9888; The automatic code check did not run for this case. Codes
                    below are unchecked model suggestions; review with extra care.
                </div>
            )}

            <p className="dx-codes-disclaimer">
                AI-suggested from the referral documents. Verify each code against the
                cited evidence; you make the final coding decision.
            </p>

            {primary && primaryUnacceptable && (
                <div className="dx-codes-warning dx-codes-warning--primary">
                    &#9888; {primary.code} is not acceptable as a primary diagnosis
                    under PDGM. Medicare returns the claim until it is recoded.
                    Select a documented, acceptable primary diagnosis or query
                    the physician.
                </div>
            )}
            {primary && !primaryUnacceptable && primary.validation?.clinical_group && (
                <div className="dx-codes-primary-summary">
                    Primary: <strong>{primary.code}</strong> &mdash; clinical group{' '}
                    <strong>{primary.validation.clinical_group}</strong>
                </div>
            )}

            <div className="dx-codes-list">
                {candidates.map((cand, index) => {
                    if (!cand.code) {
                        if (!cand.missing_fact) return null;
                        return (
                            <div key={index} className="dx-code-item dx-code-item--gap">
                                <div className="dx-code-gap-label">Needs documentation</div>
                                {cand.verification?.original_code && (
                                    <div className="dx-code-retracted">
                                        Proposed {cand.verification.original_code} was
                                        withdrawn by the automatic code check
                                    </div>
                                )}
                                <div className="dx-code-gap-fact">{cand.missing_fact}</div>
                                {(cand.audit_flags || []).map((flag, flagIndex) => (
                                    <div
                                        key={flagIndex}
                                        className="dx-codes-warning dx-codes-warning--audit"
                                    >
                                        &#9888; {flag}
                                    </div>
                                ))}
                                {cand.evidence && (
                                    <div className="dx-code-evidence">
                                        &ldquo;{cand.evidence}&rdquo;
                                        <span className="dx-code-source">
                                            {' '}&mdash; {cand.document}{cand.page ? `, p. ${cand.page}` : ''}
                                        </span>
                                    </div>
                                )}
                                {onCopyQuery && (
                                    <div className="dx-code-actions">
                                        <button
                                            className="dx-codes-copy dx-code-query-copy"
                                            onClick={() => onCopyQuery(index)}
                                        >
                                            {copiedQueryIndex === index
                                                ? 'Copied!'
                                                : 'Copy physician query'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    }

                    const status = cand.status || 'pending';
                    const invalid = !cand.validation?.valid;
                    const replacement = cand.replaced_by
                        ? candidates.find(c => c.client_id === cand.replaced_by)
                        : null;
                    return (
                        <div key={index} className={`dx-code-item dx-code-item--${status}`}>
                            <div className="dx-code-headline">
                                <span className="dx-code-code">{cand.code}</span>
                                {cand.validation?.description && (
                                    <span className="dx-code-description">{cand.validation.description}</span>
                                )}
                                {cand.source === 'user' && (
                                    <span className="dx-user-badge">Added by you</span>
                                )}
                            </div>
                            {replacement && (
                                <div className="dx-code-corrected">
                                    Replaced by {replacement.code}
                                </div>
                            )}
                            {cand.refined_from && (
                                <div className="dx-code-corrected">
                                    Refined from {cand.refined_from}
                                </div>
                            )}
                            {cand.verification?.corrected && (
                                <div className="dx-code-corrected">
                                    Adjusted from {cand.verification.original_code} by
                                    the automatic code check
                                    {cand.verification.verifier_note && (
                                        <div className="dx-code-corrected-note">
                                            {cand.verification.verifier_note}
                                        </div>
                                    )}
                                </div>
                            )}
                            {invalid && cand.validation?.description && (
                                <div className="dx-codes-warning">
                                    &#9888; {cand.code} is a category heading, not valid for
                                    claim submission &mdash; a more specific code is needed.
                                </div>
                            )}
                            {invalid && !cand.validation?.description && (
                                <div className="dx-codes-warning">
                                    &#9888; This code was not found in the FY{tableFy} ICD-10-CM
                                    code set &mdash; do not use it without coder review.
                                </div>
                            )}
                            {(cand.audit_flags || []).map((flag, flagIndex) => (
                                <div
                                    key={flagIndex}
                                    className="dx-codes-warning dx-codes-warning--audit"
                                >
                                    &#9888; {flag}
                                </div>
                            ))}
                            {cand.validation?.clinical_group && (
                                <div className="dx-code-facts">
                                    Clinical group: {cand.validation.clinical_group}
                                    {cand.validation.acceptable_primary ? ' · acceptable as primary' : ' · not acceptable as primary'}
                                    {cand.validation.comorbidity_subgroup && (
                                        <span className="dx-comorbidity-chip">
                                            PDGM comorbidity: {cand.validation.comorbidity_subgroup}
                                        </span>
                                    )}
                                </div>
                            )}
                            {!cand.validation?.clinical_group && !invalid && (
                                <div className="dx-code-facts">
                                    Not acceptable as primary under PDGM
                                    {cand.validation?.comorbidity_subgroup && (
                                        <span className="dx-comorbidity-chip">
                                            PDGM comorbidity: {cand.validation.comorbidity_subgroup}
                                        </span>
                                    )}
                                </div>
                            )}
                            {cand.evidence_status === 'pending' && (
                                <div className="dx-code-evidence-pending">
                                    <ClipLoader size={12} /> Searching documents for
                                    supporting evidence&hellip;
                                </div>
                            )}
                            {cand.evidence_status === 'not_found' && (
                                <div className="dx-code-evidence-missing">
                                    No supporting evidence found in the uploaded
                                    documents. Verify before billing.
                                </div>
                            )}
                            {(cand.evidence_status === 'timeout' || cand.evidence_status === 'error') && (
                                <div className="dx-code-evidence-missing">
                                    Evidence search {cand.evidence_status === 'error' ? 'failed' : 'timed out'}.
                                    {onRetryEvidence && (
                                        <button
                                            className="dx-codes-copy dx-code-evidence-retry"
                                            onClick={() => onRetryEvidence(index)}
                                        >
                                            Retry
                                        </button>
                                    )}
                                </div>
                            )}
                            {cand.evidence && (
                                <div className="dx-code-evidence">
                                    &ldquo;{cand.evidence}&rdquo;
                                    <span className="dx-code-source">
                                        {' '}&mdash; {cand.document}{cand.page ? `, p. ${cand.page}` : ''}
                                    </span>
                                </div>
                            )}
                            {cand.rationale && (
                                <div className="dx-code-rationale">{cand.rationale}</div>
                            )}
                            <div className="dx-code-actions">
                                {onRefine && (
                                    <button
                                        className="dx-code-btn"
                                        onClick={() => onRefine(index)}
                                    >
                                        Refine
                                    </button>
                                )}
                                {onCodeReview && (
                                    <>
                                        <button
                                            className={`dx-code-btn dx-code-btn--select${status === 'selected' ? ' active' : ''}`}
                                            onClick={() => onCodeReview(index, 'selected')}
                                        >
                                            &#10003; Select
                                        </button>
                                        <button
                                            className={`dx-code-btn dx-code-btn--dismiss${status === 'rejected' ? ' active' : ''}`}
                                            onClick={() => onCodeReview(index, 'rejected')}
                                        >
                                            &#10005; Dismiss
                                        </button>
                                    </>
                                )}
                                {status === 'selected' && (
                                    <label className="dx-code-primary-label">
                                        <input
                                            type="radio"
                                            name="dx-primary"
                                            checked={primaryIndex === index}
                                            onChange={() => onDesignatePrimary?.(index)}
                                        />
                                        Primary diagnosis
                                        {!(cand.validation?.acceptable_primary) && (
                                            <span className="dx-primary-hint">
                                                not acceptable as primary
                                            </span>
                                        )}
                                    </label>
                                )}
                                {(status === 'selected' || status === 'rejected') && (
                                    <ReviewStamp review={cand.review} />
                                )}
                                {status === 'selected' && primaryIndex === index && (
                                    <ReviewStamp review={diagnosisCodes.primary_review} verb="Primary set" />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {gaps.length > 0 && (
                <p className="dx-codes-gap-hint">
                    {gaps.length} finding{gaps.length !== 1 ? 's' : ''} need
                    {gaps.length === 1 ? 's' : ''} more documentation before a specific
                    code can be assigned.
                </p>
            )}
        </div>
    );
}
